import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Mic, MicOff, Video, VideoOff, PhoneOff, MessageSquare, Users, Check, X, Copy, Send, DoorOpen, Crown, CircleDot } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fetchIceServers, makeWsUrl, newPeerConnection } from "../lib/webrtc";
import { useMediaStream } from "../lib/useMediaStream";
import StreamWatermark from "../components/StreamWatermark";
import CameraPermissionGate from "../components/CameraPermissionGate";

/**
 * Mesh conference room.
 *
 * Deadlock-prevention: when peer A sees peer B enter "admitted" state, only the
 * one with the GREATER user_id initiates the offer. Both sides install handlers
 * immediately so the answering peer is always ready.
 */
export default function ConferenceRoom() {
  const { mid } = useParams();
  const [search] = useSearchParams();
  const inviteToken = search.get("invite") || "";
  const { user, token } = useAuth();
  const nav = useNavigate();

  const [meeting, setMeeting] = useState(null);
  const [self, setSelf] = useState(null);          // { user_id, name, is_host, state }
  const [roster, setRoster] = useState([]);       // peers admitted
  const [waitingQueue, setWaitingQueue] = useState([]);
  const [chat, setChat] = useState([]);
  const [chatMsg, setChatMsg] = useState("");
  const [mediaState, setMediaState] = useState({}); // {peer_id: {cam, mic}}
  const media = useMediaStream();
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [showChat, setShowChat] = useState(true);
  const [showPeople, setShowPeople] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const wsRef = useRef(null);
  const iceServersRef = useRef(null);
  const pcsRef = useRef({});        // { peer_id: { pc, videoEl } }
  const videoRefs = useRef({});     // { peer_id: HTMLVideoElement }
  const localVideoRef = useRef(null);

  // --- Recording (host-only, local stream only) ---
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [uploadState, setUploadState] = useState("idle");

  const isAdmitted = self?.state === "admitted";

  // ---- entry flow ----
  useEffect(() => {
    if (!token) { setLoadErr("Sign in to join the meeting."); return; }
    (async () => {
      iceServersRef.current = await fetchIceServers();
      await media.acquire();   // will trigger permission prompt
      connectWs();
    })();
    return cleanup;
    // eslint-disable-next-line
  }, [mid]);

  // Bind local preview + hot-swap tracks on every peer when media changes
  useEffect(() => {
    if (media.stream && localVideoRef.current) {
      localVideoRef.current.srcObject = media.stream;
      localVideoRef.current.muted = true;
    }
    if (media.stream) {
      Object.entries(pcsRef.current).forEach(([pid, rec]) => {
        const senders = rec.pc.getSenders();
        media.stream.getTracks().forEach((track) => {
          const sender = senders.find((s) => s.track?.kind === track.kind);
          if (sender) sender.replaceTrack(track).catch(() => {});
          else rec.pc.addTrack(track, media.stream);
        });
      });
    }
  }, [media.stream]);

  const cleanup = () => {
    Object.values(pcsRef.current).forEach(({ pc }) => { try { pc.close(); } catch {} });
    pcsRef.current = {};
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    media.stop();
  };

  const connectWs = () => {
    const url = makeWsUrl(`/api/ws/meeting/${mid}?token=${encodeURIComponent(token)}${inviteToken ? `&invite=${encodeURIComponent(inviteToken)}` : ""}`);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onclose = () => {};
    ws.onerror = () => setLoadErr("Connection lost");

    ws.onmessage = async (evt) => {
      const msg = safeJson(evt.data);
      if (!msg) return;
      if (msg.type === "error") { setLoadErr(msg.payload?.detail || "Error"); return; }

      if (msg.type === "welcome") {
        setMeeting(msg.payload.meeting);
        setSelf(msg.payload.self);
        const admitted = (msg.payload.roster || []).filter((p) => p.state === "admitted" && p.user_id !== msg.payload.self.user_id);
        setRoster(admitted);
        const waiting = (msg.payload.roster || []).filter((p) => p.state === "waiting");
        setWaitingQueue(waiting);
        setChat(msg.payload.chat_log || []);
        if (msg.payload.self.state === "admitted") {
          // Announce readiness so existing peers can offer us
          send({ type: "ready" });
          // And for any existing admitted peers, if our user_id is greater, we offer them
          for (const p of admitted) {
            await maybeInitiate(p.user_id, msg.payload.self.user_id);
          }
        }
      } else if (msg.type === "admitted") {
        setSelf((prev) => prev ? { ...prev, state: "admitted" } : prev);
        const r = (msg.payload?.roster || []).filter((p) => p.user_id !== self?.user_id);
        setRoster(r);
        send({ type: "ready" });
        for (const p of r) {
          await maybeInitiate(p.user_id, self?.user_id);
        }
      } else if (msg.type === "rejected") {
        setLoadErr("The host rejected your request to join.");
      } else if (msg.type === "kicked") {
        setLoadErr("You were removed from this meeting.");
      } else if (msg.type === "waiting_room") {
        const p = msg.payload?.peer;
        if (p) setWaitingQueue((prev) => [...prev.filter((x) => x.user_id !== p.user_id), p]);
      } else if (msg.type === "peer_joined") {
        const p = msg.payload?.peer;
        if (!p || p.user_id === self?.user_id) return;
        setRoster((prev) => [...prev.filter((x) => x.user_id !== p.user_id), p]);
        setWaitingQueue((prev) => prev.filter((x) => x.user_id !== p.user_id));
      } else if (msg.type === "peer_ready") {
        // Another peer is ready. If our user_id > theirs → we offer.
        const otherId = msg.payload?.peer_id;
        if (otherId) await maybeInitiate(otherId, self?.user_id);
      } else if (msg.type === "peer_left") {
        const pid = msg.payload?.peer_id;
        closePeer(pid);
        setRoster((prev) => prev.filter((x) => x.user_id !== pid));
        setWaitingQueue((prev) => prev.filter((x) => x.user_id !== pid));
      } else if (msg.type === "offer") {
        await handleOffer(msg.from, msg.payload);
      } else if (msg.type === "answer") {
        const rec = pcsRef.current[msg.from];
        if (rec) { try { await rec.pc.setRemoteDescription(new RTCSessionDescription(msg.payload)); } catch {} }
      } else if (msg.type === "ice") {
        const rec = pcsRef.current[msg.from];
        if (rec) { try { await rec.pc.addIceCandidate(msg.payload); } catch {} }
      } else if (msg.type === "chat") {
        setChat((prev) => [...prev.slice(-200), msg.payload]);
      } else if (msg.type === "media_state") {
        setMediaState((prev) => ({ ...prev, [msg.from]: msg.payload }));
      } else if (msg.type === "meeting_ended" || msg.type === "host_left") {
        setLoadErr(msg.type === "host_left" ? "The host has left." : "The meeting has ended.");
        cleanup();
      }
    };
  };

  const send = (frame) => { if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(frame)); };

  // ---- peer connection management ----
  const ensurePc = (peerId) => {
    if (pcsRef.current[peerId]) return pcsRef.current[peerId].pc;
    const pc = newPeerConnection(iceServersRef.current);
    pcsRef.current[peerId] = { pc };

    // Always attach any local tracks so an incoming offer can be answered
    if (media.stream) {
      media.stream.getTracks().forEach((t) => pc.addTrack(t, media.stream));
    }
    pc.ontrack = (e) => {
      const el = videoRefs.current[peerId];
      if (el && el.srcObject !== e.streams[0]) el.srcObject = e.streams[0];
    };
    pc.onicecandidate = (e) => {
      if (e.candidate) send({ type: "ice", to: peerId, payload: e.candidate.toJSON() });
    };
    return pc;
  };

  const closePeer = (peerId) => {
    const rec = pcsRef.current[peerId];
    if (!rec) return;
    try { rec.pc.close(); } catch {}
    delete pcsRef.current[peerId];
  };

  const maybeInitiate = async (otherId, ownId) => {
    // Deadlock rule: only peer with GREATER user_id sends the offer.
    if (!ownId || !otherId) return;
    if (ownId <= otherId) return;
    const pc = ensurePc(otherId);
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      send({ type: "offer", to: otherId, payload: { type: offer.type, sdp: offer.sdp } });
    } catch {}
  };

  const handleOffer = async (fromId, payload) => {
    const pc = ensurePc(fromId);
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(payload));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      send({ type: "answer", to: fromId, payload: { type: answer.type, sdp: answer.sdp } });
    } catch {}
  };

  // ---- host controls ----
  const admit = (peerId) => send({ type: "admit", to: peerId });
  const reject = (peerId) => send({ type: "reject", to: peerId });
  const kick = (peerId) => send({ type: "kick", to: peerId });

  // ---- local controls ----
  const toggleCam = () => {
    const t = media.stream?.getVideoTracks?.()[0];
    if (!t) return;
    t.enabled = !t.enabled; setCamOn(t.enabled);
    send({ type: "media_state", payload: { cam: t.enabled, mic: micOn } });
  };
  const toggleMic = () => {
    const t = media.stream?.getAudioTracks?.()[0];
    if (!t) return;
    t.enabled = !t.enabled; setMicOn(t.enabled);
    send({ type: "media_state", payload: { cam: camOn, mic: t.enabled } });
  };

  const endOrLeave = async () => {
    if (self?.is_host) {
      if (!window.confirm("End meeting for all participants?")) return;
      if (recording) stopRecording();
      send({ type: "end_meeting" });
    }
    cleanup();
    nav("/conference");
  };

  // ---- Recording (host captures local stream) ----
  const pickMime = () => {
    const candidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
    for (const c of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  };

  const startRecording = () => {
    if (!media.stream) { toast.error("Camera not ready"); return; }
    if (typeof MediaRecorder === "undefined") { toast.error("Recording not supported in this browser"); return; }
    try {
      chunksRef.current = [];
      const mime = pickMime();
      const rec = new MediaRecorder(media.stream, mime ? { mimeType: mime, videoBitsPerSecond: 2_500_000 } : undefined);
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mime || "video/webm" });
        chunksRef.current = [];
        if (!blob.size) return;
        try {
          setUploadState("uploading");
          toast("Uploading meeting recording…");
          const ext = (mime.split("/")[1] || "webm").split(";")[0];
          const form = new FormData();
          form.append("file", new File([blob], `meeting-${mid}.${ext}`, { type: mime }));
          const { data } = await api.post(`/meetings/${mid}/recording-upload`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000,
          });
          setUploadState("saved");
          toast.success("Recording saved");
        } catch (e) {
          setUploadState("failed");
          toast.error("Recording upload failed: " + (e?.response?.data?.detail || e?.message || "unknown"));
        }
      };
      rec.start(5000);
      recorderRef.current = rec;
      setRecording(true);
      toast.success("Recording started");
    } catch (e) {
      toast.error("Could not start recording: " + (e?.message || e));
    }
  };

  const stopRecording = () => {
    try {
      const rec = recorderRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
    } catch {}
    recorderRef.current = null;
    setRecording(false);
  };

  const copyInvite = () => {
    if (!meeting) return;
    // Host can copy link directly; for public meetings we append the token hint
    const base = `${window.location.origin}/conference/${meeting.id}`;
    navigator.clipboard.writeText(base);
    toast.success("Meeting link copied");
  };

  const sendChat = (e) => {
    e.preventDefault();
    if (!chatMsg.trim()) return;
    send({ type: "chat", payload: { text: chatMsg.trim() } });
    setChatMsg("");
  };

  const tiles = useMemo(() => {
    // build tile grid: self + roster
    const items = [];
    items.push({ peer: { ...(self || {}), name: self?.name || "You", is_self: true }, mediaOn: { cam: camOn, mic: micOn } });
    roster.forEach((p) => {
      const m = mediaState[p.user_id] || { cam: true, mic: true };
      items.push({ peer: p, mediaOn: m });
    });
    return items;
  }, [self, roster, mediaState, camOn, micOn]);

  if (loadErr) {
    return (
      <div data-testid="conference-error" className="pt-32 pb-20 px-4 max-w-xl mx-auto text-center">
        <div className="tv-glass rounded-2xl border border-red-500/30 p-10">
          <div className="text-red-300 font-display font-black text-3xl">Meeting unavailable</div>
          <div className="text-white/70 mt-2">{loadErr}</div>
          <button onClick={() => nav("/conference")} className="mt-6 bg-tv-accent text-black font-bold px-5 py-2.5 rounded-full text-sm">Back to meetings</button>
        </div>
      </div>
    );
  }

  if (!isAdmitted) {
    return (
      <div data-testid="conference-waiting" className="pt-32 pb-20 px-4 max-w-xl mx-auto text-center">
        <div className="tv-glass rounded-2xl border border-white/10 p-10">
          <DoorOpen className="w-8 h-8 text-tv-accent mx-auto" />
          <div className="font-display font-black text-3xl text-white mt-2">Waiting for the host…</div>
          <div className="text-white/70 mt-2">You'll join <b className="text-white">{meeting?.title || "this meeting"}</b> as soon as the host admits you.</div>
          {(media.state === "denied" || media.state === "error") && (
            <div className="mt-5 bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-left">
              <div className="text-red-300 font-bold text-sm">Camera not available</div>
              <div className="text-white/70 text-xs mt-1">{media.error}</div>
              <button onClick={() => media.acquire({ force: true })} data-testid="conference-retry-camera" className="mt-3 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                Try again
              </button>
            </div>
          )}
          <button onClick={() => nav("/conference")} className="mt-6 text-white/60 hover:text-white text-sm underline">Cancel</button>
        </div>
      </div>
    );
  }

  const gridCols = tiles.length <= 1 ? "grid-cols-1" : tiles.length <= 4 ? "grid-cols-2" : tiles.length <= 9 ? "grid-cols-3" : "grid-cols-4";

  return (
    <div data-testid="conference-room" className="pt-28 min-h-screen bg-black flex flex-col">
      <div className="px-4 md:px-6 py-3 flex items-center gap-3 flex-wrap border-b border-white/10">
        <div className="font-display font-bold text-white flex items-center gap-2">
          {self?.is_host && <Crown className="w-4 h-4 text-tv-accent" />} {meeting?.title}
        </div>
        <div className="text-xs uppercase tracking-wider text-white/40">{meeting?.kind}</div>
        <div className="flex-1" />
        <button onClick={copyInvite} data-testid="conference-copy-link" className="tv-glass px-3 py-1.5 rounded-full text-xs text-white font-semibold flex items-center gap-1.5">
          <Copy className="w-3 h-3" /> Copy link
        </button>
        <button onClick={() => setShowPeople((s) => !s)} data-testid="conference-toggle-people" className="tv-glass px-3 py-1.5 rounded-full text-xs text-white font-semibold flex items-center gap-1.5">
          <Users className="w-3 h-3" /> {tiles.length}
        </button>
        <button onClick={() => setShowChat((s) => !s)} data-testid="conference-toggle-chat" className="tv-glass px-3 py-1.5 rounded-full text-xs text-white font-semibold flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3" /> Chat
        </button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 p-3 md:p-4 overflow-auto relative">
          {(media.state === "requesting" || media.state === "denied" || media.state === "error") && (
            <CameraPermissionGate
              state={media.state}
              error={media.error}
              onAcquire={() => media.acquire({ force: true })}
              devices={media.devices}
              current={media.current}
              onSelect={media.selectDevice}
              onFlip={media.flipCamera}
              primaryLabel="Enable camera"
            />
          )}
          {media.state === "ready" && (
            <CameraPermissionGate
              state="ready"
              devices={media.devices}
              current={media.current}
              onSelect={media.selectDevice}
              onFlip={media.flipCamera}
            />
          )}
          <div className={`grid ${gridCols} gap-3 h-full auto-rows-fr`}>
            {tiles.map(({ peer, mediaOn }) => (
              <div key={peer.user_id} data-testid={`conference-tile-${peer.user_id}`} className="relative bg-[#0B1120] rounded-xl overflow-hidden border border-white/10 min-h-[180px]">
                {peer.is_self ? (
                  <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                ) : (
                  <video
                    ref={(el) => { if (el) videoRefs.current[peer.user_id] = el; }}
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                )}
                {!mediaOn.cam && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/85">
                    <div className="w-16 h-16 rounded-full bg-tv-brand text-black font-display font-black text-2xl flex items-center justify-center">
                      {peer.name?.[0] || "?"}
                    </div>
                  </div>
                )}
                <StreamWatermark variant="main" size="sm" />
                <div className="absolute bottom-3 left-3 flex items-center gap-2">
                  <span className="bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded">{peer.is_self ? "You" : peer.name}</span>
                  {peer.is_host && <span className="bg-tv-accent text-black text-[10px] font-bold px-1.5 py-0.5 rounded">HOST</span>}
                  {!mediaOn.mic && <span className="bg-red-500/80 text-white p-1 rounded"><MicOff className="w-3 h-3" /></span>}
                </div>
                {self?.is_host && !peer.is_self && (
                  <button onClick={() => kick(peer.user_id)} data-testid={`conference-kick-${peer.user_id}`} className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">Kick</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {showPeople && (
          <aside className="w-72 border-l border-white/10 bg-[#0B1120]/80 overflow-y-auto p-3">
            <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2">People ({tiles.length})</div>
            {self?.is_host && waitingQueue.length > 0 && (
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-wider text-tv-accent font-bold mb-1.5">Waiting room ({waitingQueue.length})</div>
                {waitingQueue.map((p) => (
                  <div key={p.user_id} data-testid={`conference-waiting-${p.user_id}`} className="flex items-center gap-2 py-1.5">
                    <div className="w-7 h-7 rounded-full bg-white/10 text-white font-bold text-xs flex items-center justify-center">{p.name?.[0]}</div>
                    <div className="flex-1 text-white text-sm truncate">{p.name}</div>
                    <button onClick={() => admit(p.user_id)} data-testid={`conference-admit-${p.user_id}`} className="bg-tv-accent text-black text-[10px] font-bold p-1.5 rounded-full"><Check className="w-3 h-3" /></button>
                    <button onClick={() => reject(p.user_id)} data-testid={`conference-reject-${p.user_id}`} className="bg-red-500/80 text-white text-[10px] p-1.5 rounded-full"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1.5">In the room</div>
            {tiles.map(({ peer }) => (
              <div key={peer.user_id} className="flex items-center gap-2 py-1.5">
                <div className="w-7 h-7 rounded-full bg-tv-brand text-black font-bold text-xs flex items-center justify-center">{peer.name?.[0]}</div>
                <div className="flex-1 text-white text-sm">{peer.is_self ? "You" : peer.name}{peer.is_host && " · host"}</div>
              </div>
            ))}
          </aside>
        )}

        {showChat && (
          <aside className="w-72 border-l border-white/10 bg-[#0B1120]/80 flex flex-col">
            <div className="px-3 py-2 border-b border-white/10 text-white font-display font-bold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Chat</div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5 text-sm">
              {chat.length === 0 && <div className="text-white/40 text-xs">No messages yet.</div>}
              {chat.map((m) => (
                <div key={m.id} className="flex gap-2 items-start" data-testid={`conference-chat-${m.id}`}>
                  <div className="text-tv-brand font-semibold text-xs">{m.name}:</div>
                  <div className="text-white/85">{m.text}</div>
                </div>
              ))}
            </div>
            <form onSubmit={sendChat} className="p-2 border-t border-white/10 flex gap-2">
              <input data-testid="conference-chat-input" value={chatMsg} onChange={(e) => setChatMsg(e.target.value)} placeholder="Message the room" className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-sm text-white outline-none" />
              <button className="bg-tv-accent text-black rounded-full px-3"><Send className="w-3.5 h-3.5" /></button>
            </form>
          </aside>
        )}
      </div>

      <div className="border-t border-white/10 bg-[#0B1120]/90 py-3 flex items-center justify-center gap-3">
        <button data-testid="conference-toggle-mic" onClick={toggleMic} className={`w-11 h-11 rounded-full flex items-center justify-center ${micOn ? "bg-white/10 text-white hover:bg-white/20" : "bg-red-500/80 text-white"}`}>
          {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </button>
        <button data-testid="conference-toggle-cam" onClick={toggleCam} className={`w-11 h-11 rounded-full flex items-center justify-center ${camOn ? "bg-white/10 text-white hover:bg-white/20" : "bg-red-500/80 text-white"}`}>
          {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </button>
        {self?.is_host && meeting?.allow_recording && (
          recording ? (
            <button
              data-testid="conference-stop-record"
              onClick={stopRecording}
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-2.5 rounded-full flex items-center gap-2"
              title="Stop recording"
            >
              <CircleDot className="w-4 h-4 animate-pulse" /> REC
            </button>
          ) : (
            <button
              data-testid="conference-start-record"
              onClick={startRecording}
              className="bg-white/10 hover:bg-white/20 text-white font-semibold px-4 py-2.5 rounded-full flex items-center gap-2"
              title="Start recording (host only)"
            >
              <CircleDot className="w-4 h-4" /> Record
            </button>
          )
        )}
        <button data-testid="conference-leave" onClick={endOrLeave} className="bg-tv-live hover:bg-red-600 text-white font-bold px-5 py-2.5 rounded-full flex items-center gap-2">
          <PhoneOff className="w-4 h-4" /> {self?.is_host ? "End for all" : "Leave"}
        </button>
        {uploadState === "uploading" && <span className="text-xs text-white/60" data-testid="conference-recording-upload">Uploading recording…</span>}
      </div>
    </div>
  );
}

function safeJson(s) { try { return JSON.parse(s); } catch { return null; } }
